import { useCallback, useSyncExternalStore } from "react";

import type {
  ZarvisKnowledgeScope,
  ZarvisMode,
  ZarvisResearchIntent,
  ZarvisTurnResponse,
  ZarvisWorkflowState,
} from "./zarvisApi";
import {
  createAgentActivityRunState,
  type AgentActivityRunState,
} from "./agentActivity";

export type ZarvisBackgroundTurnStatus = "pending" | "completed" | "error";

/** A receiver must attach promptly; a detached view cannot monitor global access indefinitely. */
export const ZARVIS_TURN_HANDOFF_TIMEOUT_MS = 10_000;

export type ZarvisHandoffGlobalAccess = "checking" | "allowed" | "denied";

/**
 * The small piece of turn state that must outlive a routed tray panel.
 *
 * The registry is deliberately session-memory only. Conversation durability still belongs to the
 * server/query cache; this only prevents a route swap from cancelling an in-flight request and lets
 * the returning mounted surface reconstruct its pending/completed row.
 */
export interface ZarvisBackgroundTurnSnapshot {
  requestId: number;
  displayTurnId: number;
  actorId: string;
  workspaceId: string;
  scopeNodeId: string;
  knowledgeScope: ZarvisKnowledgeScope;
  question: string;
  requestQuestion: string;
  imagePromptBase?: string;
  mode: ZarvisMode;
  requestedMode: ZarvisMode;
  researchIntent: ZarvisResearchIntent;
  researchOfferTurnId?: string;
  status: ZarvisBackgroundTurnStatus;
  handoffPending: boolean;
  activity: AgentActivityRunState;
  startedAt: string;
  finishedAt?: string;
  conversationId: string | null;
  conversationStatus: "active" | "archived";
  workflowState: ZarvisWorkflowState | null;
  storedTurnId?: string;
  storedTurnSequence?: number;
  storedImageAvailable?: boolean;
  response?: ZarvisTurnResponse;
  error?: string;
}

interface ZarvisBackgroundTurnEntry {
  controller: AbortController;
  snapshot: ZarvisBackgroundTurnSnapshot;
  handoffTimer?: ReturnType<typeof setTimeout>;
  handoffExpiresAt?: number;
}

interface BeginZarvisBackgroundTurnInput {
  actorId: string;
  workspaceId: string;
  scopeNodeId: string;
  knowledgeScope: ZarvisKnowledgeScope;
  question: string;
  requestQuestion?: string;
  imagePromptBase?: string;
  mode: ZarvisMode;
  requestedMode?: ZarvisMode;
  researchIntent?: ZarvisResearchIntent;
  researchOfferTurnId?: string;
  conversationId: string | null;
  conversationStatus: "active" | "archived";
  workflowState: ZarvisWorkflowState | null;
  activity?: AgentActivityRunState;
}

interface CompleteZarvisBackgroundTurnInput {
  conversationId: string;
  conversationStatus: "active" | "archived";
  workflowState: ZarvisWorkflowState | null;
  storedTurnId: string;
  storedTurnSequence: number;
  storedImageAvailable?: boolean;
  response: ZarvisTurnResponse;
  activity: AgentActivityRunState;
}

export interface ZarvisBackgroundTurnHandle {
  requestId: number;
  displayTurnId: number;
  controller: AbortController;
  updateConversation: (
    conversationId: string,
    conversationStatus: "active" | "archived",
  ) => void;
  updateActivity: (activity: AgentActivityRunState) => void;
  complete: (result: CompleteZarvisBackgroundTurnInput) => void;
  fail: (error: string, activity: AgentActivityRunState) => void;
}

const entries = new Map<string, ZarvisBackgroundTurnEntry>();
const listeners = new Map<string, Set<() => void>>();
let nextRequestId = 0;
let nextDisplayTurnId = 0;

function registryKey(actorId: string, scopeNodeId: string): string {
  return `${actorId}\u0000${scopeNodeId}`;
}

function emit(key: string): void {
  for (const listener of listeners.get(key) ?? []) listener();
}

function updateEntry(
  key: string,
  requestId: number,
  update: (current: ZarvisBackgroundTurnSnapshot) => ZarvisBackgroundTurnSnapshot,
): void {
  const entry = entries.get(key);
  if (!entry || entry.snapshot.requestId !== requestId) return;
  entry.snapshot = update(entry.snapshot);
  emit(key);
}

export function beginZarvisBackgroundTurn(
  input: BeginZarvisBackgroundTurnInput,
): ZarvisBackgroundTurnHandle | null {
  const key = registryKey(input.actorId, input.scopeNodeId);
  if (entries.has(key)) return null;

  const controller = new AbortController();
  const requestId = ++nextRequestId;
  // Server turn sequences are positive. Negative client-only ids cannot collide with restored rows.
  const displayTurnId = --nextDisplayTurnId;
  entries.set(key, {
    controller,
    snapshot: {
      requestId,
      displayTurnId,
      actorId: input.actorId,
      workspaceId: input.workspaceId,
      scopeNodeId: input.scopeNodeId,
      knowledgeScope: input.knowledgeScope,
      question: input.question,
      requestQuestion: input.requestQuestion ?? input.question,
      imagePromptBase: input.imagePromptBase,
      mode: input.mode,
      requestedMode: input.requestedMode ?? input.mode,
      researchIntent: input.researchIntent ?? "standard",
      researchOfferTurnId: input.researchOfferTurnId,
      status: "pending",
      handoffPending: false,
      activity: input.activity ?? createAgentActivityRunState(`background-${requestId}`),
      startedAt: new Date().toISOString(),
      conversationId: input.conversationId,
      conversationStatus: input.conversationStatus,
      workflowState: input.workflowState,
    },
  });
  emit(key);

  return {
    requestId,
    displayTurnId,
    controller,
    updateConversation: (conversationId, conversationStatus) => {
      updateEntry(key, requestId, (current) => ({
        ...current,
        conversationId,
        conversationStatus,
      }));
    },
    updateActivity: (activity) => {
      updateEntry(key, requestId, (current) => ({ ...current, activity }));
    },
    complete: (result) => {
      updateEntry(key, requestId, (current) => ({
        ...current,
        ...result,
        mode: result.response.mode,
        status: "completed",
        finishedAt: new Date().toISOString(),
        error: undefined,
      }));
    },
    fail: (error, activity) => {
      updateEntry(key, requestId, (current) => ({
        ...current,
        status: "error",
        activity,
        finishedAt: new Date().toISOString(),
        error,
        response: undefined,
      }));
    },
  };
}

export function getZarvisBackgroundTurn(
  actorId: string,
  scopeNodeId: string,
): ZarvisBackgroundTurnSnapshot | null {
  return entries.get(registryKey(actorId, scopeNodeId))?.snapshot ?? null;
}

/** Transfer this exact request across a drawer unmount without starting a second request. */
export function beginZarvisBackgroundTurnHandoff(
  actorId: string,
  scopeNodeId: string,
  requestId: number,
): boolean {
  const key = registryKey(actorId, scopeNodeId);
  const entry = entries.get(key);
  if (!entry || entry.snapshot.requestId !== requestId || entry.controller.signal.aborted) {
    return false;
  }
  // A repeated maximize action must not prolong the period without an access monitor.
  if (entry.snapshot.handoffPending) {
    if (entry.handoffExpiresAt !== undefined && Date.now() >= entry.handoffExpiresAt) {
      cancelZarvisBackgroundTurn(actorId, scopeNodeId);
      return false;
    }
    return true;
  }
  entry.snapshot = { ...entry.snapshot, handoffPending: true };
  entry.handoffExpiresAt = Date.now() + ZARVIS_TURN_HANDOFF_TIMEOUT_MS;
  entry.handoffTimer = setTimeout(() => {
    if (entries.get(key) !== entry || !entry.snapshot.handoffPending) return;
    cancelZarvisBackgroundTurn(actorId, scopeNodeId);
  }, ZARVIS_TURN_HANDOFF_TIMEOUT_MS);
  emit(key);
  return true;
}

/** The mounted receiver supplies its fresh access result before showing transferred global data. */
export function acceptZarvisBackgroundTurnHandoff(
  actorId: string,
  scopeNodeId: string,
  requestId: number,
  globalAccess: ZarvisHandoffGlobalAccess,
): boolean {
  const key = registryKey(actorId, scopeNodeId);
  const entry = entries.get(key);
  if (!entry || entry.snapshot.requestId !== requestId || entry.controller.signal.aborted) {
    return false;
  }
  if (entry.handoffExpiresAt !== undefined && Date.now() >= entry.handoffExpiresAt) {
    cancelZarvisBackgroundTurn(actorId, scopeNodeId);
    return false;
  }
  if (entry.snapshot.knowledgeScope === "global") {
    if (globalAccess === "denied") {
      cancelZarvisBackgroundTurn(actorId, scopeNodeId);
      return false;
    }
    if (globalAccess !== "allowed") return false;
  }
  if (!entry.snapshot.handoffPending) return true;
  clearTimeout(entry.handoffTimer);
  entry.handoffTimer = undefined;
  entry.handoffExpiresAt = undefined;
  entry.snapshot = { ...entry.snapshot, handoffPending: false };
  emit(key);
  return true;
}

export function consumeZarvisBackgroundTurn(
  actorId: string,
  scopeNodeId: string,
  requestId: number,
): void {
  const key = registryKey(actorId, scopeNodeId);
  const entry = entries.get(key);
  if (
    !entry
    || entry.snapshot.requestId !== requestId
    || entry.snapshot.status === "pending"
    || entry.snapshot.handoffPending
  ) return;
  clearTimeout(entry.handoffTimer);
  entries.delete(key);
  emit(key);
}

export function cancelZarvisBackgroundTurn(actorId: string, scopeNodeId: string): void {
  const key = registryKey(actorId, scopeNodeId);
  const entry = entries.get(key);
  if (!entry) return;
  clearTimeout(entry.handoffTimer);
  entry.controller.abort(new DOMException("Zarvis turn was cancelled.", "AbortError"));
  entries.delete(key);
  emit(key);
}

/** Close/remove is scoped by the mounted app key, while the registry also keys by actor. */
export function cancelZarvisBackgroundTurnsForScope(scopeNodeId: string): void {
  for (const [key, entry] of entries) {
    if (entry.snapshot.scopeNodeId !== scopeNodeId) continue;
    clearTimeout(entry.handoffTimer);
    entry.controller.abort(new DOMException("Zarvis tray tab was closed.", "AbortError"));
    entries.delete(key);
    emit(key);
  }
}

/** Closing a workspace-grouped Zarvis tab cancels every turn started from that workspace. */
export function cancelZarvisBackgroundTurnsForWorkspace(workspaceId: string): void {
  for (const [key, entry] of entries) {
    if (entry.snapshot.workspaceId !== workspaceId) continue;
    clearTimeout(entry.handoffTimer);
    entry.controller.abort(new DOMException("Zarvis workspace tab was closed.", "AbortError"));
    entries.delete(key);
    emit(key);
  }
}

/** A role downgrade invalidates every in-flight global turn owned by the same actor. */
export function cancelZarvisGlobalBackgroundTurnsForActor(actorId: string): void {
  for (const [key, entry] of entries) {
    if (
      entry.snapshot.actorId !== actorId
      || entry.snapshot.knowledgeScope !== "global"
    ) continue;
    clearTimeout(entry.handoffTimer);
    entry.controller.abort(new DOMException("Global Zarvis access was removed.", "AbortError"));
    entries.delete(key);
    emit(key);
  }
}

export function clearAllZarvisBackgroundTurns(): void {
  for (const [key, entry] of entries) {
    clearTimeout(entry.handoffTimer);
    entry.controller.abort(new DOMException("Zarvis background turns were cleared.", "AbortError"));
    entries.delete(key);
    emit(key);
  }
}

function subscribeToKey(key: string | null, listener: () => void): () => void {
  if (!key) return () => undefined;
  const current = listeners.get(key) ?? new Set<() => void>();
  current.add(listener);
  listeners.set(key, current);
  return () => {
    current.delete(listener);
    if (current.size === 0) listeners.delete(key);
  };
}

/** React subscription for the mounted surface currently bound to actor + exact scope. */
export function useZarvisBackgroundTurn(
  actorId: string | null,
  scopeNodeId: string | null,
  enabled: boolean,
): ZarvisBackgroundTurnSnapshot | null {
  const key = enabled && actorId && scopeNodeId
    ? registryKey(actorId, scopeNodeId)
    : null;
  const subscribe = useCallback(
    (listener: () => void) => subscribeToKey(key, listener),
    [key],
  );
  const getSnapshot = useCallback(
    () => key ? entries.get(key)?.snapshot ?? null : null,
    [key],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
